import { CommonTexts } from './CommonTexts';
import type { DropdownPointData, DropdownPoints } from '../components/dropdown/dropdown.types';
import type { NavPointData, NavPoints } from '../components/navigation/navigation.types';
import * as Icons from '@lucide/svelte';
import { IntranetIcons } from '../modules/intranet/IntranetIcons';
import { IntranetTreeService } from '../modules/intranet/IntranetTreeService';
import type { IntranetMenuBody } from '../api/types/intranet.types';
import type { PageKey } from './page.types';
import { PageService } from './PageService.svelte';
import { UserService } from './UserService.svelte';

/**
 * Which page goes into which menu — the points themselves (label, icon, path, badges) come from PageService, so a
 * page is defined once and changes everywhere.
 */
class MenuServiceClass {
    private readonly SearchParam = 'q';
    private readonly LoginPoint: DropdownPointData = { label: CommonTexts.Login, icon: Icons.LogIn, href: '/' };

    /** The main navigation; a page's `sub` (PageService) becomes its sub navigation — only pages the user may open. */
    public get menuNavigation(): Readonly<NavPoints> {
        if (!UserService.body.isLoggedIn) {
            return { login: this.LoginPoint };
        }

        return {
            ...this.listDashboardPoints(),
            ...this.listNavPoints(this.filterAccessible(['entities', 'helpers', 'admin', 'root'])),
        };
    }

    public get menuQuick(): Readonly<DropdownPoints> {
        return UserService.body.isLoggedIn ? this.listPoints(this.filterAccessible(['dashboard', 'favorites', 'messages'])) : {};
    }

    /** Logout stays while logged in — without the team USER it is the only way out. */
    public get menuSettings(): Readonly<DropdownPoints> {
        return UserService.body.isLoggedIn ? this.listPoints([...this.filterAccessible(['user']), 'logout']) : {};
    }

    public getSearchHref(query?: string): string {
        const Path = PageService.getPath('search');
        const Query = (query ?? '').trim();

        return Query === '' ? Path : `${Path}?${new URLSearchParams({ [this.SearchParam]: Query }).toString()}`;
    }

    /** A list with this search in its field. */
    public getListSearchHref(key: PageKey, query: string): string {
        return `${PageService.getPath(key)}?${new URLSearchParams({ [this.SearchParam]: query }).toString()}`;
    }

    protected filterAccessible(keys: PageKey[]): PageKey[] {
        return keys.filter((key: PageKey): boolean => PageService.canAccess(key));
    }

    /** The dashboard with its pages and the intranets of the user as sub points. */
    protected listDashboardPoints(): NavPoints {
        if (!PageService.canAccess('dashboard')) {
            return {};
        }
        const Point = PageService.getNavPoint('dashboard');
        const Sub = { ...Point.sub, ...this.listIntranetPoints() };

        return { dashboard: Object.keys(Sub).length > 0 ? { ...Point, sub: Sub } : Point };
    }

    /** One point per intranet of the user. */
    protected listIntranetPoints(): NavPoints {
        if (!PageService.canAccess('intranet')) {
            return {};
        }

        return Object.fromEntries(
            UserService.body.intranets.map((intranet: IntranetMenuBody): [string, NavPointData] => {
                const Href = IntranetTreeService.getHref(intranet, null);

                return [
                    `intranet_${intranet.intranet_id.toString()}`,
                    { label: intranet.intranet, icon: IntranetIcons.Intranet, href: Href, active: PageService.path === Href || PageService.path.startsWith(`${Href}/`) },
                ];
            })
        );
    }

    protected listNavPoints(keys: PageKey[]): NavPoints {
        return Object.fromEntries(keys.map((key: PageKey): [PageKey, NavPointData] => [key, PageService.getNavPoint(key)]));
    }

    protected listPoints(keys: PageKey[]): DropdownPoints {
        return Object.fromEntries(keys.map((key: PageKey): [PageKey, DropdownPointData] => [key, PageService.getPoint(key)]));
    }
}

export const MenuService = new MenuServiceClass();
